import { formatOf, isConvertibleIso, toDate, toIso } from './dates'
import { absenceOf } from './field-plan'
import { type AnyZod, inspect, type Inspected, literalType, shapeEntries } from './introspect'

// Step 2 of the pipeline: turn a transport value into the declared type.
//
// Coercion exists because the transport is stringly-typed. `toConfigType` maps `z.number()` to a *string*
// config item — the config form has no numeric widget — so a number is stored and sent as `'123'`.
// Expression-bound params do the same. Without this, every numeric config item would fail its own schema.
//
// Coerce only where the intent is unambiguous; anything else is an error rather than a guess. Note that
// `z.coerce.*` is unusable here: `z.coerce.number().parse('')` is `0` and `z.coerce.boolean().parse('false')`
// is `true`. This runs AFTER absence normalization, so a blank never reaches it.

export interface Coerced {
  ok: boolean
  value?: unknown
  // The zod type token the value should have been; ../schema/messages turns it into wording.
  expected?: string
}

const ok = (value: unknown): Coerced => ({ ok: true, value })
const fail = (expected: string): Coerced => ({ ok: false, expected })

// Matched after trim + lowercase. The test for ADDING a token, so the set has a stopping rule rather than
// growing by precedent: it must have an unambiguous opposite, and the pair must both be listed. A word with
// no opposite is a STATUS, not a boolean, and belongs in a `z.enum([...])`.
//
// Widening is only safe BECAUSE an unrecognised value fails (see `toBooleanValue`). Making an unknown default
// to `false` — the partial hand-rolled allow-list this replaces — silently turns an explicit off into an on.
//
// The list is published to authors, so a change here is a change in three more places: the exhaustive pass in
// `__tests__/configs-service.test.ts`, the table in `service-code-format/02-zod-types.md` (shipped inside the
// CLI package), and the prose in `docs/guides/flow-extensions-custom/parameters-and-types.md`.
const TRUE = new Set(['true', '1', 'yes', 'y', 't', 'on', 'checked', 'enabled', 'enable', 'active'])
const FALSE = new Set(['false', '0', 'no', 'n', 'f', 'off', 'unchecked', 'disabled', 'disable', 'inactive'])

function toStringValue(value: unknown): Coerced {
  if (typeof value === 'string') {
    return ok(value)
  }

  // A number or a boolean in a text field has one obvious text form. A structure does not — `String({})` is
  // '[object Object]' and `String([1,2])` is '1,2', which are artefacts of JS rather than what was meant.
  if (typeof value === 'boolean') {
    return ok(String(value))
  }

  return typeof value === 'number' && Number.isFinite(value) ? ok(String(value)) : fail('string')
}

function toNumberValue(value: unknown): Coerced {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? ok(value) : fail('number')
  }

  if (typeof value !== 'string') {
    return fail('number')
  }

  // `Number('')` is 0 and `Number(' ')` is 0, so require actual digits. A blank has already been treated as
  // absence by this point; anything else non-numeric is a caller error.
  const parsed = Number(value.trim())

  return value.trim() !== '' && Number.isFinite(parsed) ? ok(parsed) : fail('number')
}

// `z.bigint()` wants an actual BigInt, so `Number()` would never satisfy it — a whole number is the only
// input that can be converted without losing information.
function toBigIntValue(value: unknown): Coerced {
  if (typeof value === 'bigint') {
    return ok(value)
  }

  if (typeof value === 'number') {
    return Number.isInteger(value) ? ok(BigInt(value)) : fail('bigint')
  }

  if (typeof value !== 'string' || value.trim() === '') {
    return fail('bigint')
  }

  try {
    return ok(BigInt(value.trim()))
  } catch {
    return fail('bigint')
  }
}

function toBooleanValue(value: unknown): Coerced {
  if (typeof value === 'boolean') {
    return ok(value)
  }

  if (typeof value === 'number') {
    return value === 1 ? ok(true) : value === 0 ? ok(false) : fail('boolean')
  }

  if (typeof value !== 'string') {
    return fail('boolean')
  }

  const lowered = value.trim().toLowerCase()

  return TRUE.has(lowered) ? ok(true) : FALSE.has(lowered) ? ok(false) : fail('boolean')
}

/**
 * `z.json()` — the transport carries text, the handler gets the structure.
 *
 * Parsing belongs here rather than in a `.transform()` for the reason the whole module exists: a failure has
 * to arrive as the same `Coerced` miss every other wrong-typed value produces, so it is reported against the
 * field by its label («Payload» must be valid JSON) instead of escaping as a raw `SyntaxError` that names
 * neither the extension, the method, nor the parameter.
 */
function toJsonValue(value: unknown): Coerced {
  // Already a structure. A flow can bind the parameter to an expression that evaluates to one, so the value
  // does not always arrive as text — and `JSON.parse({})` would throw on exactly the payload this type
  // exists to carry. Nothing to convert means nothing to do.
  if (typeof value !== 'string') {
    return ok(value)
  }

  try {
    return ok(JSON.parse(value))
  } catch {
    return fail('json')
  }
}

/**
 * Coerce one SCALAR `value` for a field whose core zod type is `coreType`. Types with no meaningful coercion
 * — enum, and everything the runtime does not special-case — pass through for the author's schema to judge:
 * membership is zod's job, not ours.
 *
 * Dates need the SCHEMA rather than the type name, because `z.date()`, `z.coerce.date()` and `z.iso.date()`
 * convert differently — see `coerceLeaf`.
 */
export function coerceValue(value: unknown, coreType: string): Coerced {
  if (coreType === 'string') {
    return toStringValue(value)
  }

  if (coreType === 'number') {
    return toNumberValue(value)
  }

  if (coreType === 'bigint') {
    return toBigIntValue(value)
  }

  if (coreType === 'boolean') {
    return toBooleanValue(value)
  }

  if (coreType === 'date') {
    const parsed = toDate(value)

    return parsed ? ok(parsed) : fail('date')
  }

  // An enum's options may be numbers declared as strings, so stringify a number and let zod check membership.
  if (coreType === 'enum' && typeof value === 'number') {
    return ok(String(value))
  }

  return ok(value)
}

/** Whether a schema accepts a value outright. A probe, so a throwing refinement reads as "no". */
export function accepts(schema: AnyZod, value: unknown): boolean {
  try {
    return !!(schema as unknown as { safeParse?: (v: unknown) => { success: boolean } })
      .safeParse?.(value)?.success
  } catch {
    return false
  }
}

/**
 * A union, resolved by TRYING its branches in declaration order — the first that coerces to something its
 * own branch accepts wins.
 *
 * Trying rather than picking is what keeps D1/D7 closed inside a union: a flow template supplies every value
 * as a string, so `z.union([z.boolean(), z.string()])` given `'true'` has to convert through the boolean
 * branch and succeed there. Picking the first branch blindly would hand `execute` the string.
 *
 * ⚠️ **A permissive branch shadows every branch after it.** `z.union([z.string(), z.boolean()])` given
 * `'true'` matches the string branch as-is, so the boolean branch is never reached and `execute` receives
 * `'true'`. That is the same positional rule as publishing (both read declaration order, left to right),
 * which is why it was chosen — one mental model covers both. It is documented, and pinned by a test.
 *
 * When NO branch accepts it, the value is handed back untouched so the author's own union error is what the
 * user sees. This step never invents an error.
 */
function coerceUnion(value: unknown, branches: AnyZod[]): unknown {
  for (const branch of branches) {
    const coerced = coerceDeep(value, branch)

    if (accepts(branch, coerced)) {
      return coerced
    }
  }

  return value
}

// The container types that DECLARE something about their contents, and so can be walked. Everything else —
// `unknown`, a bare `any` — declares nothing and is passed through as it arrived.
const CONTAINERS = new Set(['object', 'array', 'record'])

/**
 * The same coercion, applied at every DEPTH — inside a declared object's properties and inside a list's
 * elements, not only at the top of a field.
 *
 * Without this a declaration means different things depending on where it sits: `z.string()` takes `42` at
 * the top level but `z.array(z.string())` rejects `[42]`, and `z.object({ zip: z.string() })` rejects
 * `{ zip: 12345 }`. The transport is no more type-faithful inside a container than outside one.
 *
 * The rule is simply: **walk whatever declares its contents, convert whatever declares a type, and leave
 * everything else alone.** A `z.record(z.string(), z.unknown())` therefore coerces nothing — not because
 * records are exempt, but because `unknown` declares no type to coerce to — while
 * `z.record(z.string(), z.number())` converts its values like any other declaration.
 *
 * BEST-EFFORT below the top level: a value that cannot be converted is left exactly as it arrived, for the
 * author's own schema to reject. That keeps the failure reported against the property that actually failed
 * (`tags.1`, `address.zip`) rather than as a coercion error naming the whole field, and it keeps this step
 * incapable of making anything stricter — it only ever converts.
 *
 * Absence is resolved at every depth too, by the same rule the top level uses: ask the sub-schema which
 * absent forms it accepts, and hand it the one it wants. Without that, `.optional()` means one thing on a
 * field and another inside a list — `z.array(z.string().optional())` would reject `[null]` while a bare
 * `z.string().optional()` accepts it.
 *
 * A sub-schema that accepts NO absent form is left exactly as it arrived, so zod raises it and the message
 * is built against the property that failed (`«City» is required`) — this step never invents an error.
 */
export function coerceDeep(value: unknown, schema: AnyZod | undefined): unknown {
  if (!schema) {
    return value
  }

  const ins = inspect(schema)

  if (isBlank(value, ins, schema)) {
    return absentAs(schema, value)
  }

  if (value == null) {
    return value
  }

  // Before any type branch below: a union has no single type to walk, it has a list to try.
  if (ins.branches) {
    return coerceUnion(value, ins.branches)
  }

  if (ins.coreType === 'array') {
    const element = ins.core?._zod?.def?.element as AnyZod | undefined

    return Array.isArray(value) ? value.map(item => coerceDeep(item, element)) : value
  }

  if (ins.coreType === 'record') {
    if (typeof value !== 'object' || Array.isArray(value)) {
      return value
    }

    const valueType = ins.core?._zod?.def?.valueType as AnyZod | undefined

    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>).map(([key, item]) => [key, coerceDeep(item, valueType)])
    )
  }

  if (ins.coreType === 'object') {
    if (typeof value !== 'object' || Array.isArray(value)) {
      return value
    }

    const source = value as Record<string, unknown>
    // Only DECLARED properties are walked. An undeclared key is either stripped by zod or carried through
    // untouched by `z.looseObject`; either way the runtime has no declaration to coerce it against.
    const coerced: Record<string, unknown> = { ...source }

    for (const [name, property] of shapeEntries(ins.core)) {
      if (name in source) {
        coerced[name] = coerceDeep(source[name], property)
      }
    }

    return coerced
  }

  const result = coerceLeaf(value, ins)

  return result.ok ? result.value : value
}

/** True when a field's own type is a container this module walks rather than converts. */
export const isContainer = (coreType: string): boolean => CONTAINERS.has(coreType)

/**
 * One leaf, converted for VALIDATION.
 *
 * Everything reaches zod as the type it declared: a date arrives as a `Date` whatever form the host sent, and
 * an ISO field arrives as a string in its own declared shape. What the HANDLER receives is a separate
 * question — a `z.date()` value is restored to the original afterwards (see ../normalize.ts).
 */
export function coerceLeaf(value: unknown, ins: Inspected): Coerced {
  // A union reaches here when its first branch is a scalar (that is what `isContainer` was asked about).
  // Always `ok`: an unmatched value is passed on for the author's union to reject with its own message.
  if (ins.branches) {
    return ok(coerceUnion(value, ins.branches))
  }

  // Ahead of every type branch below: `z.json()` declares `unknown`, so there is no core type to switch on.
  if (ins.meta.frJson) {
    return toJsonValue(value)
  }

  const format = formatOf(ins)

  if (ins.coreType === 'string' && isConvertibleIso(format)) {
    const iso = toIso(value, format as string)

    if (iso !== undefined) {
      return ok(iso)
    }

    // Nothing to reshape. A STRING is handed on unchanged so the format check speaks for itself — "expects
    // an ISO date, e.g. 2024-01-01" tells the author more than "must be a date", and it is what a blank or
    // a typo deserves. A non-string could never satisfy a string field at all, so it fails here.
    return typeof value === 'string' ? ok(value) : fail('date')
  }

  // A literal publishes as the type of its value (see ./introspect.ts `deriveWidget`), so it has to CONVERT
  // to that type as well. The console draws a toggle for `z.literal(true)` and the flow supplies the string
  // `'true'` like any other boolean; publishing a typed widget without the matching conversion would leave
  // the field unfillable from a flow. A mixed literal declares no single type and is passed through.
  if (ins.coreType === 'literal') {
    const declared = literalType(ins.core)

    return declared ? coerceValue(value, declared) : ok(value)
  }

  return coerceValue(value, ins.coreType)
}

// The same "is this blank?" rule the top-level pipeline applies (see ../schema/normalize.ts): `''` is a value
// on a string unless a declared default can absorb it, and absence for every other type.
function isBlank(value: unknown, ins: Inspected, schema: AnyZod): boolean {
  if (value === undefined || value === null) {
    return true
  }

  if (value !== '') {
    return false
  }

  // A union treats a blank as absence whatever its first branch resolved to. `''` is a value only on a plain
  // string type, and a union is not one — which is what makes `z.union([z.url(), z.literal('')]).optional()`
  // mean "blank is not set", the spelling the docs give for an optional formatted field.
  if (ins.branches) {
    return true
  }

  return ins.coreType !== 'string' || absenceOf(schema).absorbsBlank
}

// Hand the schema whichever absent form it accepts. `required` returns the value untouched, leaving zod to
// report it against its own path.
function absentAs(schema: AnyZod, value: unknown): unknown {
  const { absence } = absenceOf(schema)

  if (absence === 'required') {
    return value
  }

  if (absence === 'as-received') {
    return value === '' ? undefined : value
  }

  return absence === 'null' ? null : undefined
}
